/**
 * The call context's contract: what any screen that dials, answers or shows a
 * call may ask the provider for, and the hook that asks.
 *
 * Moved out of `CallProvider.tsx` unchanged when that file passed AGENTS.md §4's
 * limit. The provider is the only thing that writes this value; the interface is
 * the surface it promises. `CallProvider.tsx` re-exports `useCallContext`, so no
 * importer had to change a line.
 */
import { createContext, useContext } from "react";
import type { RecordingStopReason, TransferMode } from "../../lib/calls";
import type { CallAudio } from "./audio/apply";
import type { CallState, CallTarget } from "./callState";
import type { VoiceReachability } from "./device/useVoiceDevice";
import type { CallQueue } from "./queue/queue";
import type { AiTakeoverTarget } from "./takeover";
import type { CallWaitingApi } from "./waiting";

export interface CallContextValue {
  state: CallState;
  /** Whether this user may use click-to-call at all (permission gate). */
  enabled: boolean;
  /**
   * Whether the dial pad may offer the business register as a source.
   *
   * Read here rather than inside the pad because the pad has no user: the call
   * window is a portal next to `<body>` and this provider is the only thing in
   * the telephony tree that was handed one.
   */
  canSearchRegister: boolean;
  /**
   * Whether a call to the office would ring in THIS window, and what is in the
   * way when it would not. `"elsewhere"` is the one worth showing loudly: it
   * means another CRM window in this browser is the one being rung.
   */
  reach: VoiceReachability;
  startCall: (target: CallTarget) => void;
  /**
   * Take a live AI call over in this browser (ring R6d): the dock's "in this
   * browser". Opens the call window on the AI's call, claims it, and walks this
   * browser into the takeover room. A no-op while another call is up, like
   * `startCall`. See `takeover/useAiTakeover.ts`.
   */
  takeOverAiCall: (target: AiTakeoverTarget) => void;
  /**
   * The list being worked down, and the three things done to it.
   *
   * One call at a time is still the normal shape - `startCall` on its own leaves
   * `queue` null - but a cold-calling desk spends most of its day on a filter of
   * two hundred rows, and this is what makes that one run instead of two hundred
   * separate errands. See `queue/queue.ts`.
   */
  queue: CallQueue | null;
  startQueue: (rows: readonly CallTarget[]) => CallQueue | null;
  nextInQueue: () => void;
  stopQueue: () => void;
  /** Answer the call being offered. */
  acceptCall: () => void;
  /** Decline it. The caller falls through to voicemail like any missed call. */
  rejectCall: () => void;
  hangup: () => void;
  toggleMute: () => void;
  /**
   * Put the customer on hold, or bring them back. A no-op on a call that was
   * not bridged as a conference - see `state.conference`, which is what the
   * button reads to disable itself with a reason.
   */
  toggleHold: () => void;
  /**
   * Ring a third party into the room - a colleague alongside the operator
   * ("add"), or the person the call is being handed to ("handover").
   *
   * A no-op on a call that was not bridged as a conference, for the same reason
   * hold is: there is no room to invite anybody into. `state.transferBlocked`
   * is what the control reads to disable itself with a reason.
   */
  startTransfer: (to: string, mode: TransferMode) => void;
  /** Take the third party off again, or call it off before they answer. */
  stopTransfer: () => void;
  /**
   * Stop recording this call without ending it.
   *
   * What happens when the far end takes the announcement up on its offer to say
   * so. Also takes the live co-pilot off the call - see `lib/record-stop.ts` on
   * the server for why the two cannot be separated. `state.recordingStopBlocked`
   * is what the control reads to disable itself with a reason.
   */
  stopRecording: (reason: RecordingStopReason) => void;
  sendDigit: (digit: string) => void;
  /** Close the modal from a terminal phase (ended/error). */
  dismiss: () => void;
  /**
   * The two audio streams of the running call, for the live co-pilot's tap.
   *
   * An accessor rather than state: these are the Voice SDK's own objects, they
   * are not immutable, and putting them in `CallState` would re-render every
   * consumer of this context for something only one of them can use. Null until
   * the call is up, and null again the moment it is not.
   */
  getCallStreams: () => { local: MediaStream | null; remote: MediaStream | null } | null;
  /**
   * Twilio's audio routing, for the panel that lets the operator change it.
   *
   * Handed out as the SDK's own object rather than as state for the same reason
   * the streams are: it is not immutable, only one consumer can use it, and it
   * belongs to a `Device` that outlives no more than the session.
   */
  getCallAudio: () => CallAudio | null;
  /**
   * A second call during a call: the knock that is ringing, the call on hold,
   * and the three things done to them. See `waiting/useCallWaiting.ts`.
   */
  waiting: CallWaitingApi;
  /** The dial pad, which any page can raise. Separate from `state.phase`: the
   *  pad is open before there is a call and closed while one is running. */
  dialerOpen: boolean;
  dialerPrefill?: string;
  openDialer: (prefill?: string) => void;
  closeDialer: () => void;
}

export const CallContext = createContext<CallContextValue | null>(null);

export function useCallContext(): CallContextValue | null {
  return useContext(CallContext);
}
